import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSColorBg,
  getCSSColorText,
  getCSSGridTemplateRows,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import NavItem from '~/components/Layout/Header/Desktop/NavItem';
import { NavContext } from '~/contexts/NavContext';
import type { NavItemType } from '~/types/NavItemType';
import type { LinkValueInterface } from '~/types/AppData/Layout/LinkValueInterface';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getHeaderHeight } from '~/utilities/getHeaderHeight';
import { useIsSI } from '~/utilities/useIsSI';

const dropdownClasses = cva(
  'fixed left-0 py-2 px-2.5 grid grid-flow-col auto-rows-min overflow gap-y-1 gap-x-4 w-full max-h-[75vh] md:w-auto md:left-auto',
  {
    variants: {
      color: getCSSColorText(),
      background: getCSSColorBg(),
      rows: getCSSGridTemplateRows(),
      sizeSm: getCSSSizeSmText(),
      level: {
        0: 'fixed left-0 w-full',
        1: '',
      },
      active: {
        false: '',
        true: '',
      },
      isSI: {
        true: '',
      },
    },
    compoundVariants: [
      {
        level: 0,
        isSI: true,
        class: 'w-full md:w-auto md:left-auto',
      },
      {
        level: 0,
        active: false,
        class: 'hidden',
      },
    ],
  }
);

const getChildrenArray = (
  megaMenu: LinkValueInterface[]
): LinkValueInterface[] => {
  const result: LinkValueInterface[] = [];
  let parent: LinkValueInterface | null = null;
  megaMenu.forEach((item) => {
    if (item.link.url === '') {
      parent = item;
      parent.children = [];
      result.push(parent);
    } else if (parent) {
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(item);
    }
  });
  return result;
};

export default component$(
  ({ item, activeItem, level }: NavItemType): JSX.Element => {
    const {
      payloadToSSR: { isMobileViewer, layout },
      themeComponents,
    } = useContext(AppDataContext);
    const { theme, isDivisionsDropdown } = useContext(NavContext);
    const clampedLevel = level >= 1 ? 1 : 0;
    const isSI = useIsSI();

    const divisionsChildren = getChildrenArray(item.children || []);

    // Determine how many divisions go in each column if we want a max of 3 columns
    const divisionsPerColumn = Math.ceil(
      divisionsChildren.length / (isMobileViewer ? 2 : 3)
    );

    // Get the total number of rows needed for the column with the most items
    let totalRows = 0;
    for (let i = 0; i < divisionsChildren.length; i += divisionsPerColumn) {
      const columnItems = divisionsChildren.slice(i, i + divisionsPerColumn);
      let columnRows = 0;
      columnItems.forEach((division) => {
        columnRows += (division.children?.length || 0) + 2; // +2 for the division heading because it has an extra space
      });
      totalRows = Math.max(totalRows, columnRows);
    }

    const headerHeight = getHeaderHeight(themeComponents, layout, {
      screenSize: 'Small',
      isSI,

      // TODO: remove this. Needed to account for the 3rd row header, but should use theme eventually.
      ...(isSI && { add: 48 }),
    });

    const style: { top: string; gridTemplateRows?: string } = {
      top: headerHeight,
    };

    if (isDivisionsDropdown) {
      style.gridTemplateRows = `repeat(${totalRows}, minmax(0, 1fr))`;
    }

    return (
      <ul
        class={dropdownClasses({
          background: theme.SubMenuBackgroundColor.value,
          color: theme.LinkColorTypeNormal.value,
          sizeSm: theme.SubMenuLinksFontSize.value,
          level: clampedLevel,
          isSI,
          active: activeItem.value === item.link.text + item.link.url,
        })}
        style={style}
      >
        {item.children?.map((each, index) => {
          return (
            <NavItem
              key={index}
              item={each}
              activeItem={activeItem}
              level={level + 1}
              isHeading={each.link.url === '' || each.link.url === '#'}
            />
          );
        })}
      </ul>
    );
  }
);
